import { toastStore, type ToastInput } from './store';

/**
 * The call-site API for components. Fire toasts from the RESOLUTION of a
 * server request, not from UI intent:
 *
 *   import { toast } from '@/toast/useToast';
 *   try {
 *     await api.pauseWarmup(id);
 *     toast.success('Warmup paused');
 *   } catch {
 *     toast.error('Couldn’t reach the server. Try again');
 *   }
 *
 * Each helper returns the toast id (so a caller could dismiss it
 * programmatically) — but toasts carry no actions beyond their own dismiss.
 *
 * `toast` is a plain module-level singleton, not a React hook — it behaves
 * identically whether or not a `ToastProvider` is an ancestor (the provider
 * only renders the dock), so components import it directly rather than
 * calling a hook that returns the same object every render.
 */
export const toast = {
  success: (message: string, dur?: number): string =>
    toastStore.add({ tone: 'good', message, dur }),
  error: (message: string, dur?: number): string =>
    toastStore.add({ tone: 'bad', message, dur }),
  info: (message: string, dur?: number): string =>
    toastStore.add({ tone: 'info', message, dur }),
  warn: (message: string, dur?: number): string =>
    toastStore.add({ tone: 'warn', message, dur }),
  show: (input: ToastInput): string => toastStore.add(input),
};

export type ToastApi = typeof toast;
